import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
} from '@clouway/ui'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { addAnalogPort, removeAnalogPort, updateAnalogPort } from '../configEdits'
import type { AnalogPortConfig } from '../types'
import { ANALOG_PORTS, AnalogInputEditForm, type AnalogInputFormValues } from '../components/AnalogInputEditForm'
import { useConfigContext } from './ConfigLayout'

// Empty or non-numeric → null (keeps NaN out of the PUT).
function toNum(value: string): number | null {
  if (value.trim() === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

function toStr(value: number | null): string {
  return value === null || value === undefined ? '' : String(value)
}

export function AnalogInputsConfig() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { config, save, loading } = useConfigContext()
  const [editing, setEditing] = useState<{ original?: string } | null>(null)

  if (loading) return <Skeleton className="h-40 w-full" />

  const ports = config.analogPortConfigs
  const editingEntry = editing?.original !== undefined ? ports.find((p) => p.port === editing.original) : undefined
  const used = new Set(ports.map((p) => p.port))
  const availablePorts = ANALOG_PORTS.filter((p) => !used.has(p) || editingEntry?.port === p)

  const handleSubmit = (values: AnalogInputFormValues) => {
    const item: AnalogPortConfig = {
      port: values.port,
      type: values.type,
      unit: values.unit,
      noise: values.noise,
      min: toNum(values.min),
      max: toNum(values.max),
      multiplier: toNum(values.multiplier),
      // The form doesn't touch device ADC readings — preserve them on edit.
      readings: editingEntry?.readings ?? [],
    }
    const next =
      editing?.original !== undefined ? updateAnalogPort(config, editing.original, item) : addAnalogPort(config, item)
    return save(next).then(() => setEditing(null))
  }

  return (
    <div className="flex flex-col gap-3">
      {ports.length === 0 && !editing && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('trackingObjects.config.emptyAnalogs')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {ports.map((port) => (
        <Card key={port.port}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {t(`trackingObjects.config.analogPorts.${port.port}` as 'trackingObjects.config.analogPorts.analog_1')}{' '}
                · {t(`trackingObjects.config.analogInputTypes.${port.type}` as 'trackingObjects.config.analogInputTypes.rpm')}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" aria-label="edit" onClick={() => setEditing({ original: port.port })}>
                  <Pencil className="size-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" aria-label="delete">
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('trackingObjects.config.deleteAnalogPort')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('trackingObjects.config.deletePortWarning')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => save(removeAnalogPort(config, port.port))}>
                        {tCommon('ui.button.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}

      {editing ? (
        <AnalogInputEditForm
          availablePorts={availablePorts}
          initialValues={
            editingEntry
              ? {
                  port: editingEntry.port,
                  type: editingEntry.type,
                  unit: editingEntry.unit,
                  multiplier: toStr(editingEntry.multiplier),
                  min: toStr(editingEntry.min),
                  max: toStr(editingEntry.max),
                  noise: editingEntry.noise,
                }
              : undefined
          }
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <Button variant="outline" onClick={() => setEditing({})}>
          <Plus className="size-4" />
          {tCommon('trackingObjects.configs.newAnalogPort')}
        </Button>
      )}
    </div>
  )
}
